// ============================================================================
// vision.js — Verifica "fototessera" con MediaPipe Face Landmarker (client-side)
// ============================================================================
// Tutto avviene nel browser: la foto NON lascia mai il dispositivo.
// Risorse self-hosted in public/ (vendored, nessun CDN a runtime):
//   - public/js/vision_bundle.js            → bundle @mediapipe/tasks-vision
//   - public/wasm/vision_wasm_*             → runtime wasm (+ variante nosimd)
//   - public/models/face_landmarker.task    → modello Face Landmarker
// ============================================================================

const BASE = process.env.BASE_URL || '/'

const VISION_BUNDLE_URL = BASE + 'js/vision_bundle.js'
const WASM_DIR = BASE + 'wasm'
const MODEL_URL = BASE + 'models/face_landmarker.task'

// ---- Soglie di conformità (tunabili dopo la validazione con webcam) ----
export const THRESHOLDS = {
  ROLL_MAX_DEG: 12, // inclinazione laterale massima della testa (gradi)
  YAW_RATIO_MAX: 0.22, // |(dL-dR)/(dL+dR)| con distanze occhi->naso
  PITCH_DELTA_MAX: 0.18, // scostamento della posizione verticale del naso da 0.5
  FACE_H_MIN: 0.4, // altezza minima del viso (frazione dell'altezza immagine)
  FACE_H_MAX: 0.8, // altezza massima del viso
  CENTER_TOL: 0.18, // tolleranza centratura orizzontale
  EYES_Y_MIN: 0.3, // fascia verticale occhi (dall'alto dell'immagine)
  EYES_Y_MAX: 0.65,
  EYE_BLINK_MAX: 0.45, // valore blendshape "occhi aperti"
  JAW_OPEN_MAX: 0.3, // bocca chiusa
  SMILE_MAX: 0.5, // sorriso contenuto
  BROW_MAX: 0.6, // sopracciglia/sguardo neutri
  SHARPNESS_MIN: 30, // varianza del Laplaciano (immagine ridimensionata)
  BRIGHTNESS_MIN: 40, // luminosità media (0-255)
  BRIGHTNESS_MAX: 235
}

let _landmarkerPromise = null

// Carica il bundle ESM senza passare dal bundler webpack (il progetto usa
// webpack 4, fragile con questo pacchetto): new Function() evita che il
// bundler interpreti l'import dinamico (il runtime del browser lo esegue).
function loadBundle() {
  return new Function('u', 'return import(u)')(VISION_BUNDLE_URL)
}

export async function getFaceLandmarker() {
  if (!_landmarkerPromise) {
    _landmarkerPromise = (async () => {
      const vision = await loadBundle()
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_DIR)
      const options = {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
        numFaces: 5,
        runningMode: 'IMAGE'
      }
      try {
        return await vision.FaceLandmarker.createFromOptions(fileset, options)
      } catch (e) {
        // fallback CPU (dispositivi senza WebGL)
        console.log("VISION GPU ERR:", e)
        options.baseOptions.delegate = 'CPU'
        return await vision.FaceLandmarker.createFromOptions(fileset, options)
      }
    })()
  }
  return _landmarkerPromise
}

const rad2deg = r => r * 180 / Math.PI

// Angoli Eulero dalla faceTransformationMatrix (Float32Array(16), column-major).
// Convenzione R = Rz(roll)*Ry(yaw)*Rx(pitch). Usata solo a scopo diagnostico:
// la verifica di frontalità primaria è geometrica (robusta rispetto a
// segno/ordine della matrice).
export function eulerFromMatrix(m) {
  const pitch = Math.asin(m[6])
  const yaw = Math.atan2(-m[2], m[10])
  const roll = Math.atan2(-m[4], m[5])
  return { yaw: rad2deg(yaw), pitch: rad2deg(pitch), roll: rad2deg(roll) }
}

function dist(a, b) {
  return Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y))
}

// Posa geometrica dai landmark (indipendente dalla convenzione della matrice).
// Landmark utili (478 punti): 1 = punta naso, 33/263 = angoli esterni occhi,
// 152 = mento.
function poseFromLandmarks(lm) {
  const nose = lm[1]
  const eyeL = lm[33]
  const eyeR = lm[263]
  const chin = lm[152]
  // roll: angolo della linea degli occhi rispetto all'orizzontale
  const roll = rad2deg(Math.atan2(eyeR.y - eyeL.y, eyeR.x - eyeL.x))
  // yaw: rapporto delle distanze naso->occhi (frontale => ~0)
  const dL = dist(nose, eyeL)
  const dR = dist(nose, eyeR)
  const yawRatio = (dL - dR) / (dL + dR)
  // pitch: posizione verticale del naso nel tratto occhi->mento (frontale => ~0.5)
  const eyeMidY = (eyeL.y + eyeR.y) / 2
  const pitchPos = (nose.y - eyeMidY) / Math.max(1e-6, chin.y - eyeMidY)
  return { roll, yawRatio, pitchPos }
}

function bbox(lm) {
  let minX = 1, maxX = 0, minY = 1, maxY = 0
  for (const p of lm) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }
  return { minX, maxX, minY, maxY }
}

// Nitidezza (varianza del Laplaciano) e luminosità media su una versione
// ridotta dell'immagine (più veloce, valori comparabili fra scatti).
function imageStats(img) {
  const canvas = document.createElement('canvas')
  const W = 200
  const H = Math.max(1, Math.round(img.naturalHeight * W / img.naturalWidth))
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, W, H)
  const data = ctx.getImageData(0, 0, W, H).data
  const g = new Float32Array(W * H)
  let lum = 0
  for (let i = 0; i < W * H; i++) {
    const gr = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]
    g[i] = gr
    lum += gr
  }
  lum /= (W * H)
  let lap = 0, n = 0
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x
      const L = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - W] - g[i + W]
      lap += L * L
      n++
    }
  }
  return { sharpness: lap / n, brightness: lum }
}

// Verifica completa: esegue il rilevamento e applica le regole di conformità.
// image: HTMLImageElement (o canvas/video) con la foto scattata.
export async function verifySelfie(image) {
  const landmarker = await getFaceLandmarker()
  const res = await landmarker.detect(image)
  const T = THRESHOLDS
  const checks = []

  // 1) un solo volto
  const nFaces = res.faceLandmarks ? res.faceLandmarks.length : 0
  checks.push({
    id: 'volto',
    ok: nFaces === 1,
    label: "Un solo volto nell'inquadratura",
    value: nFaces === 0 ? 'Nessun volto rilevato' : nFaces + ' volti'
  })
  if (nFaces !== 1) {
    return { ok: false, checks, pose: null, stats: null }
  }

  const lm = res.faceLandmarks[0]
  const pose = poseFromLandmarks(lm)
  const box = bbox(lm)
  const faceH = box.maxY - box.minY
  const cx = (box.minX + box.maxX) / 2
  const eyeY = (lm[33].y + lm[133].y + lm[362].y + lm[263].y) / 4

  // blendshapes
  const bs = {}
  if (res.faceBlendshapes && res.faceBlendshapes.length) {
    res.faceBlendshapes[0].categories.forEach(c => { bs[c.categoryName] = c.score })
  }

  // 2) frontalità (geometrica)
  const frontalOk = Math.abs(pose.roll) <= T.ROLL_MAX_DEG &&
    Math.abs(pose.yawRatio) <= T.YAW_RATIO_MAX &&
    Math.abs(pose.pitchPos - 0.5) <= T.PITCH_DELTA_MAX
  checks.push({
    id: 'frontale',
    ok: frontalOk,
    label: 'Viso frontale',
    value: 'roll ' + Math.round(pose.roll) + '°, yaw ' + pose.yawRatio.toFixed(2) + ', pitch ' + pose.pitchPos.toFixed(2)
  })

  // 3) occhi aperti
  const blinkL = bs.eyeBlinkLeft || 0
  const blinkR = bs.eyeBlinkRight || 0
  const eyesOk = blinkL < T.EYE_BLINK_MAX && blinkR < T.EYE_BLINK_MAX
  checks.push({
    id: 'occhi',
    ok: eyesOk,
    label: 'Occhi aperti',
    value: 'blink ' + blinkL.toFixed(2) + '/' + blinkR.toFixed(2)
  })

  // 4) bocca chiusa / espressione neutra
  const jaw = bs.jawOpen || 0
  const smile = Math.max(bs.mouthSmileLeft || 0, bs.mouthSmileRight || 0)
  const mouthOk = jaw < T.JAW_OPEN_MAX && smile < T.SMILE_MAX
  checks.push({
    id: 'bocca',
    ok: mouthOk,
    label: 'Bocca chiusa, espressione neutra',
    value: 'jaw ' + jaw.toFixed(2) + ', smile ' + smile.toFixed(2)
  })

  // 5) nessuna espressione alterata (sopracciglia/sguardo)
  const brow = Math.max(
    bs.browDownLeft || 0, bs.browDownRight || 0,
    bs.eyeSquintLeft || 0, bs.eyeSquintRight || 0
  )
  const browOk = brow < T.BROW_MAX
  checks.push({
    id: 'espressione',
    ok: browOk,
    label: 'Nessuna espressione alterata',
    value: 'brow ' + brow.toFixed(2)
  })

  // 6) dimensione del viso
  const sizeOk = faceH >= T.FACE_H_MIN && faceH <= T.FACE_H_MAX
  checks.push({
    id: 'dimensione',
    ok: sizeOk,
    label: 'Dimensione del viso corretta',
    value: Math.round(faceH * 100) + '%'
  })

  // 7) centratura orizzontale
  const centerOk = Math.abs(cx - 0.5) <= T.CENTER_TOL
  checks.push({
    id: 'centratura',
    ok: centerOk,
    label: 'Viso centrato',
    value: Math.round(cx * 100) + '%'
  })

  // 8) fascia verticale degli occhi
  const eyesPosOk = eyeY >= T.EYES_Y_MIN && eyeY <= T.EYES_Y_MAX
  checks.push({
    id: 'occhipos',
    ok: eyesPosOk,
    label: 'Occhi nella fascia corretta',
    value: Math.round(eyeY * 100) + '%'
  })

  // 9) nitidezza
  const stats = imageStats(image)
  const sharpOk = stats.sharpness >= T.SHARPNESS_MIN
  checks.push({
    id: 'nitidezza',
    ok: sharpOk,
    label: 'Immagine nitida',
    value: 'lap ' + Math.round(stats.sharpness)
  })

  // 10) luminosità
  const brightOk = stats.brightness >= T.BRIGHTNESS_MIN && stats.brightness <= T.BRIGHTNESS_MAX
  checks.push({
    id: 'luminosita',
    ok: brightOk,
    label: 'Luminosità corretta',
    value: Math.round(stats.brightness)
  })

  // diagnostica (utile per tarare le soglie)
  let poseMatrix = null
  if (res.faceTransformationMatrixes && res.faceTransformationMatrixes.length) {
    const mx = res.faceTransformationMatrixes[0]
    poseMatrix = eulerFromMatrix(mx.data || mx)
  }
  console.log("SELFIE VERIFY:", { pose, poseMatrix, stats })

  const ok = checks.every(c => c.ok)
  return { ok, checks, pose, stats }
}


